import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Sparkles,
  Check,
  X as XIcon,
  ExternalLink,
  AlertTriangle,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../components/AdminShell";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminProductSeeder — /admin/products/seed
 *
 * The agentic catalog growth tool. Founder types a brief in plain
 * English; we ask GPT-4o to brainstorm 8 candidate products, then
 * call Tavily to verify each one exists on Amazon. Survivors land in
 * the pending queue at the bottom of this page for one-tap
 * approve/reject.
 *
 * Three reasons the brief-driven flow beats a structured form:
 *   1. Founder thinks in merchandiser language ("hydrating concealers
 *      for deeper skin"), not in our category enum.
 *   2. The agent can mix categories in one brief ("a primer + a
 *      foundation for the same skin type") if that's the natural ask.
 *   3. The brief is preserved on every seeded product as audit context
 *      so we know WHY each one was suggested.
 */

const EXAMPLE_BRIEFS = [
  "Hydrating concealers for deeper skin tones — under-eye brightening, beginner-friendly",
  "Affordable cream blushes that work for warm undertones — $5–15 range",
  "Long-wear matte lipsticks in MLBB nude shades for medium-deep skin",
  "Smudge-proof black eyeliners that won't bleed on hooded eyes",
  "Cruelty-free setting sprays that lock in makeup for 12+ hours",
];

const SeederPage = () => {
  const [brief, setBrief] = useState("");
  const [targetCount, setTargetCount] = useState(8);
  const [seeding, setSeeding] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPending = async () => {
    setPendingLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/products/pending`,
        { withCredentials: true },
      );
      setPending(data.items || []);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load pending products. Are you signed in as admin?",
      );
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const seed = async () => {
    if (seeding) return;
    const trimmed = brief.trim();
    if (trimmed.length < 10) {
      toast.error("Brief should be at least a short sentence.");
      return;
    }
    setSeeding(true);
    setLastResult(null);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/products/seed`,
        { brief: trimmed, target_count: targetCount },
        { withCredentials: true },
      );
      setLastResult(data);
      toast.success(
        `${data.verified} product${data.verified === 1 ? "" : "s"} verified · ${
          data.dropped || 0
        } dropped`,
      );
      await loadPending();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Seeding failed.";
      if (status === 503) {
        setError(detail);
        toast.error("Tavily not configured");
      } else {
        toast.error(detail);
      }
    } finally {
      setSeeding(false);
    }
  };

  const approve = async (id) => {
    try {
      await axios.post(
        `${API}/admin/products/${id}/approve`,
        {},
        { withCredentials: true },
      );
      setPending((prev) => prev.filter((p) => p.id !== id));
      toast.success("Approved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejecting? (optional)") ?? null;
    try {
      await axios.post(
        `${API}/admin/products/${id}/reject`,
        { reason: reason || null },
        { withCredentials: true },
      );
      setPending((prev) => prev.filter((p) => p.id !== id));
      toast.success("Rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  return (
    <AdminShell title="Seed catalog with agent">
      <div className="max-w-5xl space-y-8">
        {/* ─── Sub-nav across the Products section ─── */}
        <div className="flex items-center gap-2 text-sm">
          <Link
            to="/admin/products"
            className="px-3 py-1.5 rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition"
            data-testid="seeder-tab-overrides"
          >
            ASIN overrides
          </Link>
          <span
            className="px-3 py-1.5 rounded-full bg-[#D4B483]/20 text-[#D4B483] border border-[#D4B483]/30"
            data-testid="seeder-tab-active"
          >
            Agent seeder
          </span>
        </div>

        {/* ─── Brief composer ─── */}
        <section className="bg-[#1A1720] border border-white/8 rounded-2xl p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4B483] to-[#9B6A6C] flex items-center justify-center shrink-0">
              <Wand2 className="h-5 w-5 text-[#1A1720]" />
            </div>
            <div>
              <h2 className="text-white text-lg font-serif italic">
                Tell the agent what you want
              </h2>
              <p className="text-white/55 text-sm mt-0.5">
                GPT-4o brainstorms candidates. Tavily verifies they exist
                on Amazon. Only survivors land in your review queue —
                nothing hallucinated, nothing auto-published.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. Hydrating concealers for deeper skin tones with under-eye brightening, beginner-friendly..."
              rows={3}
              maxLength={500}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#D4B483]/40 resize-none"
              data-testid="seeder-brief-input"
            />
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-white/50">
                <span>How many to find:</span>
                <select
                  value={targetCount}
                  onChange={(e) => setTargetCount(parseInt(e.target.value))}
                  className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white text-xs"
                  data-testid="seeder-count-select"
                >
                  {[3, 5, 8, 10, 12].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span className="text-white/30">·</span>
                <span>{brief.length}/500</span>
              </div>
              <button
                onClick={seed}
                disabled={seeding || brief.trim().length < 10}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4B483] hover:bg-[#E0C295] disabled:opacity-30 disabled:cursor-not-allowed text-[#1A1720] rounded-full font-medium text-sm transition"
                data-testid="seeder-submit-btn"
              >
                {seeding ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching Amazon...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Seed catalog
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Example briefs — clickable to pre-fill */}
          <div className="pt-2 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 mb-2">
              Try an example
            </p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_BRIEFS.map((ex) => (
                <button
                  key={ex}
                  onClick={() => setBrief(ex)}
                  className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/90 rounded-full border border-white/8 transition"
                  data-testid="seeder-example-chip"
                >
                  {ex.length > 60 ? ex.slice(0, 57) + "…" : ex}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Error banner (e.g. Tavily key missing) ─── */}
        {error && (
          <div
            className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-400/30 rounded-xl text-red-200 text-sm"
            data-testid="seeder-config-error"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Setup needed</div>
              <div className="text-red-200/80 text-xs leading-relaxed">{error}</div>
            </div>
          </div>
        )}

        {/* ─── Last seed run summary ─── */}
        {lastResult && (
          <section
            className="bg-[#1A1720] border border-[#D4B483]/20 rounded-2xl p-5"
            data-testid="seeder-last-result"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4B483] mb-3">
              Last run
            </p>
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div>
                <span className="text-white font-semibold">{lastResult.brainstormed}</span>
                <span className="text-white/50 ml-1">candidates from GPT-4o</span>
              </div>
              <div className="text-white/30">→</div>
              <div>
                <span className="text-emerald-300 font-semibold">{lastResult.verified}</span>
                <span className="text-white/50 ml-1">verified on Amazon</span>
              </div>
              {lastResult.dropped > 0 && (
                <>
                  <div className="text-white/30">·</div>
                  <div>
                    <span className="text-white/45 font-semibold">{lastResult.dropped}</span>
                    <span className="text-white/40 ml-1">dropped (no ASIN found)</span>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* ─── Pending review queue ─── */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white text-lg font-serif italic">
              Pending review
              {pending.length > 0 && (
                <span className="text-[#D4B483] ml-2 text-base">({pending.length})</span>
              )}
            </h2>
            <button
              onClick={loadPending}
              className="text-xs text-white/50 hover:text-white/80 transition"
              data-testid="seeder-refresh-btn"
            >
              Refresh
            </button>
          </div>

          {pendingLoading ? (
            <div className="flex items-center justify-center py-12 text-white/40">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : pending.length === 0 ? (
            <div className="text-center py-12 text-white/40 text-sm bg-[#1A1720]/50 border border-white/5 rounded-2xl">
              No products pending review. Run a brief above to seed some.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pending.map((p) => (
                <PendingCard
                  key={p.id}
                  product={p}
                  onApprove={() => approve(p.id)}
                  onReject={() => reject(p.id)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
};

const PendingCard = ({ product, onApprove, onReject }) => (
  <article
    className="bg-[#1A1720] border border-white/8 rounded-2xl p-4 flex gap-4"
    data-testid="seeder-pending-card"
  >
    <div className="w-24 h-24 shrink-0 bg-black/30 rounded-lg overflow-hidden flex items-center justify-center border border-white/5">
      {product.image_url ? (
        <img
          src={product.image_url}
          alt={product.title}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <span className="text-white/20 text-xs">no image</span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#D4B483]">
            {product.category}
          </div>
          <div className="text-white text-sm font-medium truncate">
            {product.brand}
          </div>
          <div className="text-white/65 text-xs truncate" title={product.title}>
            {product.title}
          </div>
        </div>
        {product.price_str && (
          <span className="text-emerald-300 text-sm font-mono shrink-0">
            {product.price_str}
          </span>
        )}
      </div>
      <p className="text-white/40 text-[11px] mt-1 leading-relaxed line-clamp-2">
        {product.reason}
      </p>
      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <a
          href={product.amazon_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[10px] text-white/50 hover:text-white/80 font-mono transition"
          data-testid="seeder-card-amazon-link"
        >
          {product.amazon_asin}
          <ExternalLink className="h-3 w-3" />
        </a>
        <div className="flex items-center gap-2">
          <button
            onClick={onReject}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-red-500/15 text-white/55 hover:text-red-300 rounded-full text-xs transition"
            data-testid="seeder-card-reject-btn"
          >
            <XIcon className="h-3 w-3" /> Reject
          </button>
          <button
            onClick={onApprove}
            className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 rounded-full text-xs font-medium transition"
            data-testid="seeder-card-approve-btn"
          >
            <Check className="h-3 w-3" /> Approve
          </button>
        </div>
      </div>
    </div>
  </article>
);

export default SeederPage;
